import { Redacted, Schema } from "effect";
import { privateDependencyFailure } from "@workerstation/shared/errors/private-cause";
import type { DesktopTheme } from "../../opencode/theme/types.ts";
import { DEFAULT_THEMES, oc2Theme } from "../../opencode/theme/default-themes.ts";
import { resolveThemeVariant, themeToCss } from "../../opencode/theme/resolve.ts";
import { resolveThemeVariantV2, themeV2ToCss } from "../../opencode/theme/v2/resolve.ts";

export const ThemeScopeId = Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]+$/)).pipe(Schema.brand("FrontendThemeScopeId"));
export type ThemeScopeId = typeof ThemeScopeId.Type;
export const ThemeSnapshot = Schema.Struct({ scopeId: ThemeScopeId, themeId: Schema.String, initialized: Schema.Boolean, error: Schema.NullOr(Schema.String) });
export type ThemeSnapshot = typeof ThemeSnapshot.Type;
export class FrontendThemeFailure extends privateDependencyFailure<FrontendThemeFailure>()("FrontendThemeFailure", "theme style") {}
export const frontendThemeFailure = (cause: unknown) => new FrontendThemeFailure({ cause: Redacted.make(cause) });
export const getTheme = (id: string): DesktopTheme => Schema.is(Schema.String)(id) && Object.hasOwn(DEFAULT_THEMES, id) ? DEFAULT_THEMES[id] : oc2Theme;
export const availableThemes = () => Object.keys(DEFAULT_THEMES).map(id => ({ id, name: DEFAULT_THEMES[id].name || id }));
export function themeCss(theme: DesktopTheme, scopeId: ThemeScopeId): string {
  const prefix = `[data-workerstation-theme="${scopeId}"] `;
  const light = resolveThemeVariant(theme.light, false), dark = resolveThemeVariant(theme.dark, true);
  const lightV2 = resolveThemeVariantV2(theme.light, false), darkV2 = resolveThemeVariantV2(theme.dark, true);
  const lightCss = `${themeToCss(light)}\n  ${themeV2ToCss(lightV2)}`;
  const darkCss = `${themeToCss(dark)}\n  ${themeV2ToCss(darkV2)}`;
  return `
${prefix}.oc-skin {
  color-scheme: light;
  --text-mix-blend-mode: multiply;

  ${lightCss}

  @media (prefers-color-scheme: dark) {
    color-scheme: dark;
    --text-mix-blend-mode: plus-lighter;

    ${darkCss}
  }
}

${prefix}.oc-skin.dark, ${prefix}.oc-skin[data-theme-mode="dark"] {
  color-scheme: dark;
  --text-mix-blend-mode: plus-lighter;

  ${darkCss}
}

${prefix}.oc-skin.light, ${prefix}.oc-skin[data-theme-mode="light"] {
  color-scheme: light;
  --text-mix-blend-mode: multiply;

  ${lightCss}
}
`;
}
